import { useEffect, useState } from 'react';
import type { ImgHTMLAttributes } from 'react';
import {
  useArtworkObjectUrlState,
  type ArtworkImageAccess,
  type ArtworkImageVariant,
} from '../../hooks/useArtworkObjectUrl';

export interface ArtworkObjectImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  artworkId: number;
  variant?: ArtworkImageVariant;
  updatedAt?: string;
  access?: ArtworkImageAccess;
  lazy?: boolean;
  /** Called when the authenticated/anonymous blob request fails before <img> can emit an error. */
  onLoadError?: () => void;
}

export function ArtworkObjectImage({
  artworkId,
  variant = 'thumbnail',
  updatedAt,
  access = 'public',
  lazy = true,
  onLoadError,
  ...imageProps
}: ArtworkObjectImageProps) {
  const [inView, setInView] = useState(!lazy);
  const [element, setElement] = useState<HTMLImageElement | null>(null);
  const objectUrlState = useArtworkObjectUrlState(artworkId, variant, updatedAt, access, inView);
  const { url: objectUrl, error } = objectUrlState;

  useEffect(() => {
    if (error) onLoadError?.();
  }, [error, onLoadError]);

  useEffect(() => {
    if (!lazy || !element) return;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, { rootMargin: '50px', threshold: 0.01 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, lazy]);

  return (
    <img
      {...imageProps}
      ref={setElement}
      src={objectUrl || undefined}
      loading={lazy ? 'lazy' : 'eager'}
      decoding={imageProps.decoding || 'async'}
    />
  );
}
